import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useUser } from '@/context/UserContext';
import axios from 'axios';
import { 
  StickyNote, 
  Trash2, 
  Languages, 
  Calculator, 
  History, 
  Palette,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COURSE_ICONS = {
  memeglish: Languages,
  memethematics: Calculator,
  memestory: History,
  memeart: Palette
};

const COURSE_COLORS = {
  memeglish: 'pump-green',
  memethematics: 'degen-purple',
  memestory: 'yellow-500',
  memeart: 'cyan-400'
};

const NotesPage = () => {
  const { user } = useUser();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user) {
      fetchNotes();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/notes/${user.id}`);
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/notes/${noteId}`);
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const filteredNotes = filter === 'all' 
    ? notes 
    : notes.filter(n => n.course_id === filter);

  const groupedNotes = filteredNotes.reduce((acc, note) => {
    if (!acc[note.course_id]) {
      acc[note.course_id] = [];
    }
    acc[note.course_id].push(note);
    return acc;
  }, {});

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <StickyNote className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="font-unbounded font-bold text-2xl text-white mb-2">My Notes</h2>
          <p className="text-zinc-400">Enroll to start taking notes during your courses</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-unbounded font-bold text-4xl text-white mb-4" data-testid="notes-title">
            MY <span className="text-pump-green">NOTES</span>
          </h1>
          <p className="text-zinc-400">
            All your notes from across your courses
          </p>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-wrap gap-2 mb-8"
        >
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'all'
                ? 'bg-pump-green text-black'
                : 'bg-pump-surface text-zinc-400 hover:text-white border border-white/10'
            }`}
            data-testid="filter-all"
          >
            All Notes ({notes.length})
          </button>
          {Object.keys(COURSE_ICONS).map((courseId) => {
            const Icon = COURSE_ICONS[courseId];
            const count = notes.filter(n => n.course_id === courseId).length;
            return (
              <button
                key={courseId}
                onClick={() => setFilter(courseId)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === courseId
                    ? `bg-${COURSE_COLORS[courseId]}/20 text-${COURSE_COLORS[courseId]} border border-${COURSE_COLORS[courseId]}/30`
                    : 'bg-pump-surface text-zinc-400 hover:text-white border border-white/10'
                }`}
                data-testid={`filter-${courseId}`}
              >
                <Icon size={16} />
                {courseId.toUpperCase()} ({count})
              </button>
            );
          })}
        </motion.div>

        {/* Notes Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-pump-green border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredNotes.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="font-bold text-xl text-white mb-2">No notes yet</h3>
            <p className="text-zinc-400">
              {filter === 'all' 
                ? 'Start taking notes during your courses'
                : `No notes for ${filter.toUpperCase()} yet`}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedNotes).map(([courseId, courseNotes]) => {
              const Icon = COURSE_ICONS[courseId];
              const color = COURSE_COLORS[courseId];
              
              return (
                <motion.div
                  key={courseId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-8 h-8 rounded-lg bg-${color}/20 flex items-center justify-center`}>
                      <Icon className={`text-${color}`} size={16} />
                    </div>
                    <h3 className="font-unbounded font-bold text-lg text-white">
                      {courseId.toUpperCase()}
                    </h3>
                    <span className="text-sm text-zinc-500">
                      {courseNotes.length} note{courseNotes.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    {courseNotes.map((note) => (
                      <motion.div
                        key={note.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="glass rounded-xl p-5 group hover:border-white/20 transition-all"
                        data-testid={`note-${note.id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium bg-${color}/20 text-${color}`}>
                              Slide {note.slide_index + 1}
                            </span>
                          </div>
                          <button
                            onClick={() => deleteNote(note.id)}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all"
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">
                          {note.content}
                        </p>
                        <p className="text-xs text-zinc-600 mt-3">
                          {new Date(note.created_at).toLocaleDateString()}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotesPage;
